import { useCallback, forwardRef, useState, useRef, useEffect } from 'react';
import { createGenericContext, createBemBlockBuilder } from '@ebsco-ui/utils';
import cn from 'classnames';
import Button from '@ebsco-ui/button';
import { jsx } from 'react/jsx-runtime';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

/**
 * Helper function to wrap blur handler. It allows us to detect when the wrapper and children lose focus (deep blur)
 */
var createBlurHandler = function createBlurHandler(ref, callback) {
  return function (event) {
    var newTargetElement = event.relatedTarget || document.activeElement; // IE-specific check, please refer to https://github.com/facebook/react/issues/3751

    if (ref.current && !ref.current.contains(newTargetElement)) {
      callback(event);
    }
  };
};

/**
 * Returns back a NodeList of focusable elements
 * that exist within the passed parent HTMLElement or doucment.body, or
 * an empty array if no parent passed.
 *
 * @param {HTMLElement} parent HTML element, doucument.body by default
 * @returns {(NodeList|Array)} The focusable elements that we can find
 */
var getAllFocusableElements = function getAllFocusableElements() {
  var parent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document.body;
  return parent.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)');
};

var _createGenericContext = createGenericContext({
  errorMessage: "Popover compound components cannot be rendered outside the Popover component"
}),
    usePopoverContext = _createGenericContext.useGenericContext,
    PopoverContextConsumer = _createGenericContext.GenericContextConsumer,
    PopoverContextProvider = _createGenericContext.GenericContextProvider;

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var usePopoverInteraction = function usePopoverInteraction(panelRef, popoverSelector) {
  var getAvailablePopovers = function getAvailablePopovers() {
    return Array.from(document.querySelectorAll(popoverSelector));
  };
  /**
   * @param {Node} relatedTarget
   * @return {{withinCurrentPopoverRef: boolean, withinAnotherPopover: boolean, outsideOfPopovers: boolean}|undefined}
   */


  var getUserInteraction = useCallback(function (relatedTarget) {
    if (!(panelRef !== null && panelRef !== void 0 && panelRef.current) || !popoverSelector) {
      return;
    }

    var withinCurrentPopoverRef = false,
        withinAnotherPopover = false;

    var _iterator = _createForOfIteratorHelper(getAvailablePopovers()),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var popover = _step.value;

        if (!popover.contains(relatedTarget)) {
          continue;
        }

        if (popover.contains(panelRef.current)) {
          withinCurrentPopoverRef = true;
          break;
        }

        withinAnotherPopover = true;
        break;
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return {
      withinCurrentPopoverRef: withinCurrentPopoverRef,
      withinAnotherPopover: withinAnotherPopover,
      outsideOfPopovers: !withinCurrentPopoverRef && !withinAnotherPopover
    };
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [panelRef]);
  return {
    getUserInteraction: getUserInteraction
  };
};

var _excluded$2 = ["children", "className"];

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var PopoverButton = function PopoverButton(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var _usePopoverContext = usePopoverContext(),
      getBlocksWith = _usePopoverContext.getBlocksWith,
      handleOnClick = _usePopoverContext.handleOnClick,
      isOpen = _usePopoverContext.isOpen,
      uniqueName = _usePopoverContext.uniqueName,
      buttonRef = _usePopoverContext.buttonRef;

  return /*#__PURE__*/jsx(Button, _objectSpread$2(_objectSpread$2(_objectSpread$2({
    ref: buttonRef,
    onMouseDown: function onMouseDown(ev) {
      ev.preventDefault();
      ev.target.focus();
    },
    className: cn(className, getBlocksWith("__button"), isOpen && getBlocksWith("__button--open")),
    "aria-haspopup": true,
    "aria-controls": "".concat(uniqueName, "-panel")
  }, isOpen && {
    "aria-expanded": true
  }), {}, {
    onClick: handleOnClick
  }, rest), {}, {
    children: children
  }));
};

var _excluded$1 = ["children", "className"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var PopoverPanel = function PopoverPanel(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var _usePopoverContext = usePopoverContext(),
      getBlocksWith = _usePopoverContext.getBlocksWith,
      isOpen = _usePopoverContext.isOpen,
      panelPlacement = _usePopoverContext.panelPlacement,
      uniqueName = _usePopoverContext.uniqueName,
      panelRef = _usePopoverContext.panelRef,
      handleOnPanelBlur = _usePopoverContext.handleOnPanelBlur;

  return /*#__PURE__*/jsx("div", _objectSpread$1(_objectSpread$1(_objectSpread$1({
    className: cn(className, getBlocksWith("__panel"), getBlocksWith("__panel--".concat(panelPlacement)), isOpen && getBlocksWith("__panel--open")),
    id: "".concat(uniqueName, "-panel"),
    ref: panelRef,
    onBlur: handleOnPanelBlur
  }, isOpen && {
    tabIndex: 0
  }), rest), {}, {
    children: isOpen && children
  }));
};

var _excluded = ["children", "className", "panelPlacement", "onToggle", "uniqueName", "elementToFocus"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var baseClassName = "eb-popover";
var Popover = forwardRef(function (_ref, ref) {
  var children = _ref.children,
      consumerClassName = _ref.className,
      _ref$panelPlacement = _ref.panelPlacement,
      panelPlacement = _ref$panelPlacement === void 0 ? "top-end" : _ref$panelPlacement,
      onToggle = _ref.onToggle,
      uniqueName = _ref.uniqueName,
      _ref$elementToFocus = _ref.elementToFocus,
      elementToFocus = _ref$elementToFocus === void 0 ? "panel" : _ref$elementToFocus,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var getBlocksWith = createBemBlockBuilder([baseClassName, consumerClassName]);
  var isButtonClickableRef = useRef(true);
  var panelRef = useRef(null);
  var buttonRef = useRef(null);
  var shouldSetFocusButtonOnClose = useRef(false);

  var _usePopoverInteractio = usePopoverInteraction(panelRef, ".".concat(baseClassName)),
      getUserInteraction = _usePopoverInteractio.getUserInteraction;

  var togglePopover = useCallback(function () {
    onToggle && onToggle(!isOpen);
    setIsOpen(!isOpen);
  }, [isOpen, onToggle]);
  useEffect(function () {
    if (isOpen && panelRef.current) {
      if (elementToFocus === "panel") {
        panelRef.current.focus();
      }

      if (elementToFocus === "first-focusable") {
        var firstFocusableElement = getAllFocusableElements(panelRef.current)[0];
        firstFocusableElement.focus();
      }

      return;
    }

    if (shouldSetFocusButtonOnClose.current && buttonRef.current) {
      buttonRef.current.focus();
    }

    shouldSetFocusButtonOnClose.current = true;
    setTimeout(function () {
      isButtonClickableRef.current = true;
    }, 200);
  }, [elementToFocus, isOpen]);

  var handleOnClick = function handleOnClick() {
    // ignore click when button was closed by panel blur event
    if (isButtonClickableRef.current) {
      togglePopover();
    }
  };

  var handleOnKeyPress = function handleOnKeyPress(event) {
    if (isOpen && event.key === "Escape") {
      event.preventDefault();
      togglePopover();
    }
  };

  var handleOnOutsideInteractions = useCallback(function (event) {
    var _userInteraction$with;

    if (!isOpen) {
      return;
    }

    if (event.relatedTarget === buttonRef.current) {
      isButtonClickableRef.current = false;
    }

    togglePopover();
    var userInteraction = getUserInteraction(event.relatedTarget); // When the blur event is firing with a relatedTarget, the user has interacted with
    // either the current panelRef, another known popover, or an element outside any known popovers.

    shouldSetFocusButtonOnClose.current = (_userInteraction$with = !(userInteraction !== null && userInteraction !== void 0 && userInteraction.withinAnotherPopover)) !== null && _userInteraction$with !== void 0 ? _userInteraction$with : false;
  }, [getUserInteraction, isOpen, togglePopover]);
  var handleOnPanelBlur = createBlurHandler(panelRef, handleOnOutsideInteractions);

  var closePopover = function closePopover() {
    setIsOpen(false);
  };

  var popoverProps = {
    handleOnClick: handleOnClick,
    getBlocksWith: getBlocksWith,
    isOpen: isOpen,
    panelPlacement: panelPlacement,
    panelRef: panelRef,
    buttonRef: buttonRef,
    handleOnPanelBlur: handleOnPanelBlur,
    uniqueName: uniqueName,
    closePopover: closePopover
  };
  return /*#__PURE__*/jsx(PopoverContextProvider, {
    value: popoverProps,
    children: /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({
      ref: ref,
      className: getBlocksWith(),
      onKeyDown: handleOnKeyPress
    }, rest), {}, {
      children: children
    }))
  });
});
Popover.Button = PopoverButton;
Popover.Button.displayName = "Popover.Button";
Popover.Panel = PopoverPanel;
Popover.Panel.displayName = "Popover.Panel";
Popover.ContextConsumer = PopoverContextConsumer;
Popover.ContextConsumer.displayName = "Popover.ContextConsumer";
Popover.useContext = usePopoverContext;
Popover.displayName = "Popover";

export { PopoverContextConsumer, PopoverContextProvider, Popover as default, usePopoverContext };
//# sourceMappingURL=popover-esm.js.map
